// app/categories/page.js
"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import CurvedBottomNav from "@/components/CurvedBottomNav";
import { useTranslations } from "next-intl";
import {
  MdArrowBack,
  MdTravelExplore,
  MdSchool,
  MdScience,
  MdLibraryMusic,
  MdSportsSoccer,
  MdPets,
  MdCake,
  MdVolunteerActivism,
  MdAccessible,
  MdCategory,
} from "react-icons/md";

const GREEN = "var(--primary)";
const GREEN_SOFT = "var(--primary-soft)";

/** Sections (message keys) + categories shown on this page */
const SECTIONS = [
  {
    title: "forSchools",
    items: [
      "School Projects",
      "Class Trips",
      "Classroom Supplies",
      "Science & STEM",
      "Music & Arts",
      "Sports & Teams",
    ],
  },
  {
    title: "forFamilies",
    items: [
      "Birthdays",
      "Pet & Animal Care",
      "Travel & Activities",
      "Community Service",
    ],
  },
  {
    title: "health",
    items: ["Special Needs Support"],
  },
  {
    title: "research",
    items: ["Medical Research", "Other Causes"],
  },
];

function iconFor(label) {
  if (label.includes("Travel")) return <MdTravelExplore className="w-5 h-5" />;
  if (label.includes("School") || label.includes("Class")) return <MdSchool className="w-5 h-5" />;
  if (label.includes("Science")) return <MdScience className="w-5 h-5" />;
  if (label.includes("Music")) return <MdLibraryMusic className="w-5 h-5" />;
  if (label.includes("Sports")) return <MdSportsSoccer className="w-5 h-5" />;
  if (label.includes("Pet")) return <MdPets className="w-5 h-5" />;
  if (label.includes("Birthday")) return <MdCake className="w-5 h-5" />;
  if (label.includes("Needs")) return <MdAccessible className="w-5 h-5" />;
  if (label.includes("Community")) return <MdVolunteerActivism className="w-5 h-5" />;
  return <MdCategory className="w-5 h-5" />;
}

export default function CategoriesPage() {
  const router = useRouter();
  const t = useTranslations("Categories");
  const tc = useTranslations("Common");
  const tTheme = useTranslations("Themes");

  return (
    <div className="bg-white rounded-[30px] max-w-md mx-auto min-h-screen p-4 lg:max-w-2xl lg:min-h-0 lg:my-8 lg:p-8 lg:rounded-3xl lg:border lg:border-gray-200 lg:shadow-sm pb-32">
      {/* Header */}
      <header className="flex items-center justify-between mb-4">
        <button onClick={() => router.back()} className="p-2" aria-label={tc("back")}>
          <MdArrowBack className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-gray-900">{t("title")}</h1>
        <span />
      </header>

      {/* Sections */}
      <div className="space-y-6">
        {SECTIONS.map((section) => (
          <section key={section.title}>
            <h2 className="text-sm font-semibold text-gray-900 mb-3">{t(section.title)}</h2>
            <div className="grid grid-cols-2 gap-3">
              {section.items.map((label) => (
                <Link
                  key={label}
                  href={`/campaigns?category=${encodeURIComponent(label)}`}
                  className="flex items-center gap-3 border border-gray-200 rounded-xl p-3 hover:shadow-sm transition"
                >
                  <div
                    className="w-10 h-10 rounded-full grid place-items-center"
                    style={{ backgroundColor: GREEN_SOFT }}
                  >
                    <span style={{ color: GREEN }}>{iconFor(label)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{tTheme.has(label) ? tTheme(label) : label}</p>
                    <p className="text-xs text-gray-500">{t("seeAll")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Curved Bottom Navigation */}
      <CurvedBottomNav />
    </div>
  );
}
